import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  input,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import { DesignNodeViewComponent } from '../panels/canvas/design-node-view.component';
import type { DesignNode } from '../design-builder.types';
import type { DesignScreen } from '../utils/design-publish.utils';
import { autoLayoutStyles } from '../utils/design-style.utils';
import { DesignIconActionsService, flattenScreen } from '../services/design-icon-actions.service';
import { FormsModule } from '@angular/forms';

/** Breathing room around a thumbnail's content, in host px. */
const THUMBNAIL_PADDING = 8;
/** Space kept under the lowest element when a page is trimmed to its content, in design px. */
const CONTENT_BOTTOM_PADDING = 12;

/**
 * How the screen is sized to the space it is given.
 *
 * - `contain` — whole screen visible, centred, one uniform scale. Thumbnails.
 * - `fit-width` — Preview and the published page: the screen resized to the
 *   width it is given, as one piece. A taller page scrolls down; it never
 *   scrolls sideways.
 */
export type ScreenPreviewMode = 'contain' | 'fit-width' | 'native';

/** Renders one publishable screen without any editor artboard or selection UI. */
@Component({
  selector: 'app-design-screen-preview',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, DesignNodeViewComponent],
  providers: [DesignIconActionsService],
  templateUrl: './design-screen-preview.component.html',
  styleUrl: './design-screen-preview.component.scss',
})
export class DesignScreenPreviewComponent implements AfterViewInit, OnDestroy {
  readonly screen = input.required<DesignScreen>();
  readonly mode = input<ScreenPreviewMode>('contain');
  readonly actions = inject(DesignIconActionsService);
  readonly filterQuery = signal('');
  readonly filterTiles = computed(() => {
    const ids = new Set(flattenScreen(this.screen()).map((node) => node.id));
    return (this.actions.runtime?.tiles() ?? []).filter((tile) => ids.has(tile.id) && !!tile.dropdownControl);
  });
  filterOptions(options: string[]): string[] {
    const query = this.filterQuery().trim().toLowerCase();
    return query ? options.filter((option) => option.toLowerCase().includes(query)) : options;
  }
  changeFilter(nodeId: string, value: string): void {
    this.actions.runtime?.onDropdownChange(nodeId, value);
  }
  runIcon(node: DesignNode): void {
    this.filterQuery.set('');
    void this.actions.execute(node, this.screen());
  }
  dropIconFile(event: { node: DesignNode; files: File[] }): void {
    this.actions.uploadFiles(event.node, this.screen(), event.files);
  }
  dismissDashboardError(): void {
    this.actions.runtime?.dashboardError.set('');
  }

  private readonly host = viewChild.required<ElementRef<HTMLElement>>('host');
  private readonly stage = viewChild<ElementRef<HTMLElement>>('stage');
  private readonly availableSize = signal({ width: 0, height: 0 });
  /** Laid-out height of an auto-layout screen's stage (it flows, so it is measured). */
  private readonly flowHeight = signal(0);
  private resizeObserver?: ResizeObserver;
  private hostElement?: HTMLElement;

  readonly isFitWidth = computed(() => this.mode() === 'fit-width');
  readonly isNative = computed(() => this.mode() === 'native');

  readonly childrenInFlow = computed(() => {
    const layout = this.screen().layout;
    return !!layout && layout.mode !== 'none';
  });

  /**
   * Bounds of everything drawn, children included — a group's children can
   * hang outside its stored box, and must not be trimmed off the page.
   */
  private readonly contentBounds = computed(() => {
    const screen = this.screen();
    const bounds = deepBounds(screen.nodes);
    return bounds && bounds.width > 0 && bounds.height > 0
      ? bounds
      : { x: 0, y: 0, width: screen.width, height: screen.height };
  });

  /**
   * The page height shown in fit-width: down to the lowest element (plus a
   * margin), never the frame's full height — a 1000px screen with 430px of
   * content otherwise ends in a screenful of blank page. Auto-layout screens
   * size from their flow, so they keep the frame's height.
   */
  readonly pageHeight = computed(() => {
    const screen = this.screen();
    if (!this.isFitWidth() || this.childrenInFlow() || screen.fixedHeight || !screen.nodes.length) return screen.height;
    const bounds = this.contentBounds();
    const bottom = bounds.y + bounds.height + CONTENT_BOTTOM_PADDING;
    return Math.max(1, Math.min(screen.height, Math.ceil(bottom)));
  });

  /**
   * The page width shown in fit-width: up to the right edge of the content,
   * with the same margin on the right as on the left — never the frame's full
   * width. A 2000px frame whose content stops at 1560px otherwise leaves a
   * band of empty page on the right. Only trims (never wider than the frame),
   * and the whole design still scales as one, so nothing is stretched.
   * Auto-layout screens flow across the frame, so they keep its width.
   */
  readonly pageWidth = computed(() => {
    const screen = this.screen();
    if (!this.isFitWidth() || this.childrenInFlow() || screen.fixedWidth || !screen.nodes.length) return screen.width;
    const bounds = this.contentBounds();
    const right = bounds.x + bounds.width + Math.max(0, bounds.x);
    return Math.max(1, Math.min(screen.width, Math.ceil(right)));
  });

  // ── Scale ─────────────────────────────────────────────────────────────────

  /**
   * ONE factor for the whole screen.
   *
   * Fit-width resizes the design to the width it is given — exactly what the
   * builder canvas does at its zoom level, which is why the published page is
   * the design rather than a re-flowed version of it. Stretching the axes
   * independently (or leaving the fonts at their design size) re-wraps every
   * text box and breaks the layout.
   *
   * Thumbnails instead frame the content, never magnified past 1:1.
   */
  readonly scale = computed(() => {
    const screen = this.screen();
    const { width, height } = this.availableSize();
    if (!width || !height) return 1;

    if (this.isFitWidth()) return width / Math.max(1, this.pageWidth());
    if (this.isNative()) return Math.max(Math.min(width / Math.max(1, screen.width), height / Math.max(1, screen.height), 1), 0.01);

    const bounds = this.contentBounds();
    const padding = THUMBNAIL_PADDING * 2;
    return Math.max(
      Math.min(
        (width - padding) / Math.max(1, bounds.width),
        (height - padding) / Math.max(1, bounds.height),
        1,
      ),
      0.01,
    );
  });

  readonly nodes = computed<DesignNode[]>(() => this.screen().nodes);

  readonly stageStyles = computed<Record<string, string>>(() => {
    const screen = this.screen();
    const scale = this.scale();

    const base: Record<string, string> = this.isFitWidth()
      ? {
          width: `${this.pageWidth()}px`,
          // Free-positioned pages end at their content; a flowing stage grows
          // with its rows, so it gets a floor and is measured afterwards.
          ...(this.childrenInFlow()
            ? { 'min-height': `${screen.height}px` }
            : { height: `${this.pageHeight()}px` }),
          background: screen.background,
          transform: `scale(${scale})`,
        }
      : {
          width: `${screen.width}px`,
          height: `${this.pageHeight()}px`,
          background: screen.background,
          transform: this.containTransform(scale),
        };

    return screen.layout?.mode && screen.layout.mode !== 'none'
      ? { ...base, ...autoLayoutStyles(screen.layout) }
      : base;
  });

  /** Put the CONTENT's centre in the middle of the host, not the page's. */
  private containTransform(scale: number): string {
    const { width, height } = this.availableSize();
    if (this.isNative()) {
      const screen = this.screen();
      return `translate(${(width - screen.width * scale) / 2}px, ${(height - screen.height * scale) / 2}px) scale(${scale})`;
    }
    const bounds = this.contentBounds();
    const x = width / 2 - (bounds.x + bounds.width / 2) * scale;
    const y = height / 2 - (bounds.y + bounds.height / 2) * scale;
    return `translate(${x}px, ${y}px) scale(${scale})`;
  }

  /**
   * The scaled stage's real box: a transform does not affect layout, so the
   * page would have nothing to scroll without it.
   */
  readonly scrollerStyles = computed<Record<string, string>>(() => {
    if (!this.isFitWidth()) return {} as Record<string, string>;
    const laidOut = this.flowHeight() || this.pageHeight();
    return { height: `${Math.round(laidOut * this.scale())}px` };
  });

  constructor() {
    // ECharts only re-measures on window resize; nudge it once the scaled boxes
    // are in the DOM. A synthetic event leaves the host size unchanged, so this
    // cannot loop.
    effect(() => {
      this.scale();
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    });

    // Another screen (published tabs): start it at the top.
    effect(() => {
      this.screen().id;
      untracked(() => this.hostElement?.scrollTo({ top: 0, left: 0 }));
    });

    // An auto-layout stage flows, so its height is read back from the DOM.
    effect((onCleanup) => {
      const element = this.stage()?.nativeElement;
      if (!element) return;
      const observer = new ResizeObserver(() => this.flowHeight.set(element.offsetHeight));
      observer.observe(element);
      onCleanup(() => observer.disconnect());
    });
  }

  // Keyed by id so a resize re-sizes existing widgets instead of re-creating them.
  trackNode(_index: number, node: DesignNode): string { return node.id; }

  ngAfterViewInit(): void {
    const host = this.host().nativeElement;
    this.hostElement = host;
    const update = (): void => {
      this.availableSize.set({ width: host.clientWidth, height: host.clientHeight });
    };
    update();
    this.resizeObserver = new ResizeObserver(update);
    this.resizeObserver.observe(host);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }
}

/** Union of every node's box, children included (child boxes are parent-relative). */
function deepBounds(nodes: readonly DesignNode[]): { x: number; y: number; width: number; height: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const visit = (list: readonly DesignNode[], offsetX: number, offsetY: number): void => {
    for (const node of list) {
      if (node.visible === false) continue;
      const x = offsetX + node.x;
      const y = offsetY + node.y;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + node.width);
      maxY = Math.max(maxY, y + node.height);
      if (node.children?.length) visit(node.children, x, y);
    }
  };
  visit(nodes, 0, 0);
  return Number.isFinite(minX) ? { x: minX, y: minY, width: maxX - minX, height: maxY - minY } : null;
}
